import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TriangleAlert, Truck } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import JobPhotos from '../../components/JobPhotos.jsx';
import { problem } from '../bookings/util.js';

// Operations > Incidents: everything engineers have reported on jobs
// (utility strikes, damage, injuries...), newest first, with their photos.
// ?open=<id> (the link in the alert email) highlights one.
export default function Incidents() {
  const [params] = useSearchParams();
  const open = params.get('open');
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api('/incidents').then(setList, (e) => setError(problem(e)));
  }, []);
  useEffect(() => {
    if (open && list) document.getElementById(`inc-${open}`)?.scrollIntoView({ block: 'start' });
  }, [open, list]);

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Incidents</h1>
            <p className="muted">Reported by engineers on jobs: utility strikes, damage, injuries and near misses. The team&apos;s supervisor and operations are alerted when one comes in; the supervisor decides what happens next.</p>
          </div>
        </div>
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!list && !error && <p className="muted">Loading…</p>}
        {list && !list.length && <p className="muted">No incidents reported.</p>}
      </section>
      {list?.map((i) => <IncidentCard key={i.id} incident={i} highlight={i.id === open} />)}
    </div>
  );
}

// One incident, as a card. Used on this page and on a booking.
export function IncidentCard({ incident: i, highlight = false, compact = false }) {
  const what = i.service ? `${i.type} (${i.service})` : i.type;
  return (
    <section id={`inc-${i.id}`} className={`card incident-card${highlight ? ' is-open' : ''}`}>
      <div className="incident-head">
        <TriangleAlert size={20} aria-hidden="true" />
        <div>
          <h2>{i.reference}: {what}</h2>
          <p className="small muted">{formatDateTime(i.happenedAt)} · {i.reportedByEmail}</p>
        </div>
      </div>
      <div className="incident-tags">
        <span className={`status ${i.hurt ? 'status-aborted' : 'status-completed'}`}>{i.hurt ? 'Someone hurt' : 'Nobody hurt'}</span>
        <span className={`status ${i.canContinue ? 'status-completed' : 'status-aborted'}`}>{i.canContinue ? 'Job could carry on' : 'Job couldn’t carry on'}</span>
      </div>
      <p className="incident-details">{i.details}</p>
      {!compact && (
        <p className="small">
          Job <Link to={`/bookings/list?booking=${i.bookingId}`}>{i.bookingReference}</Link> · {i.postcode}{i.teamName ? ` · ${i.teamName}` : ''}
        </p>
      )}
      {i.photos > 0 && <JobPhotos base={`/bookings/${i.bookingId}/photos`} incidentId={i.id} title="Photos" />}
    </section>
  );
}

// A booking's incidents (in the booking pop-up).
export function BookingIncidents({ bookingId }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    api(`/incidents?bookingId=${bookingId}`).then(setList, () => setList([]));
  }, [bookingId]);
  if (!list.length) return null;
  return (
    <div className="booking-incidents">
      <h3>Incidents ({list.length})</h3>
      {list.map((i) => <IncidentCard key={i.id} incident={i} compact />)}
    </div>
  );
}

// A booking's support requests (waste van, tipper...), in the booking pop-up.
export function BookingSupport({ bookingId }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    api(`/support?bookingId=${bookingId}`).then(setList, () => setList([]));
  }, [bookingId]);
  if (!list.length) return null;
  return (
    <div className="booking-incidents">
      <h3>Support requested ({list.length})</h3>
      {list.map((r) => (
        <p key={r.id} className="support-line">
          <Truck size={16} aria-hidden="true" />
          <span><strong>{r.reference}: {r.type}</strong>. {formatDateTime(r.requestedAt)} · {r.requestedByEmail}{r.notes ? <span className="small muted support-notes">{r.notes}</span> : null}</span>
        </p>
      ))}
    </div>
  );
}
